import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr } from "@/lib/api";

const BLANK = {
  name: "", title: "", category: "Executive Assistant", location: "", timezone: "", hourly_rate: 20, experience_years: 2,
  availability: "Full-time", status: "Available", english_level: "Fluent", languages: "", skills: "", tools: "",
  summary: "", work_history: "", education: "", photo_url: "",
};
const CATS = ["Executive Assistant", "Operations", "Customer Support", "Development", "Sales", "Marketing", "Bookkeeping", "Design", "Admin Support"];

const toForm = (c) => ({
  ...BLANK, ...c,
  languages: (c.languages || []).join(", "), skills: (c.skills || []).join(", "), tools: (c.tools || []).join(", "),
  work_history: (c.work_history || []).map((w) => [w.role, w.company, w.period, w.description].join(" | ")).join("\n"),
  education: (c.education || []).map((e) => [e.degree, e.school, e.year].join(" | ")).join("\n"),
});
const list = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const rows = (s, keys) => s.split("\n").filter((l) => l.trim()).map((l) => Object.fromEntries(keys.map((k, i) => [k, (l.split("|")[i] || "").trim()])));
const toPayload = (f) => ({
  ...f, hourly_rate: Number(f.hourly_rate), experience_years: Number(f.experience_years),
  languages: list(f.languages), skills: list(f.skills), tools: list(f.tools),
  work_history: rows(f.work_history, ["role", "company", "period", "description"]),
  education: rows(f.education, ["degree", "school", "year"]),
});

const F = ({ label, children, span }) => (
  <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label className="text-[11px] font-mono uppercase tracking-wider text-stone-500">{label}</Label>{children}</div>
);
const Sel = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
  </Select>
);

export const CATEGORIES = CATS;

export const CandidateForm = ({ open, onOpenChange, candidate, onSaved, saveFn, selfMode = false }) => {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(candidate ? toForm(candidate) : BLANK); }, [open, candidate]);
  const inp = (k, props = {}) => <Input data-testid={`cf-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...props} />;
  const ta = (k, rowsN, ph) => <Textarea data-testid={`cf-${k}`} rows={rowsN} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />;

  const save = async () => {
    if (!f.name || !f.title) return toast.error("Name and title are required");
    setBusy(true);
    try {
      const payload = toPayload(f);
      const { data } = saveFn ? await saveFn(payload) : candidate ? await api.put(`/admin/candidates/${candidate.id}`, payload) : await api.post("/admin/candidates", payload);
      toast.success(candidate ? "Candidate updated" : "Candidate added");
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="candidate-form-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{candidate ? "Edit candidate" : "Add candidate"}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {!selfMode && <F label="Full name">{inp("name")}</F>}
          <F label="Job title">{inp("title")}</F>
          {!selfMode && <F label="Category"><Sel value={f.category} onChange={(v) => setF({ ...f, category: v })} options={Array.from(new Set([...CATS, f.category]))} testId="cf-category" /></F>}
          {!selfMode && <F label="Status"><Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} options={["Available", "Interviewing", "Placed"]} testId="cf-status" /></F>}
          <F label="Hourly rate (USD)">{inp("hourly_rate", { type: "number" })}</F>
          <F label="Years of experience">{inp("experience_years", { type: "number" })}</F>
          <F label="Availability"><Sel value={f.availability} onChange={(v) => setF({ ...f, availability: v })} options={["Full-time", "Part-time"]} testId="cf-availability" /></F>
          <F label="English level"><Sel value={f.english_level} onChange={(v) => setF({ ...f, english_level: v })} options={["Native", "Fluent", "Advanced", "Intermediate"]} testId="cf-english" /></F>
          <F label="Location">{inp("location", { placeholder: "City, Country" })}</F>
          <F label="Timezone">{inp("timezone", { placeholder: "PHT (UTC+8)" })}</F>
          <F label="Skills (comma separated)" span>{inp("skills")}</F>
          <F label="Tools (comma separated)">{inp("tools")}</F>
          <F label="Languages (comma separated)">{inp("languages")}</F>
          <F label="Photo URL (or upload from the table)" span>{inp("photo_url", { placeholder: "https://…" })}</F>
          <F label="Summary" span>{ta("summary", 3)}</F>
          <F label="Work history — one per line: Role | Company | Period | Description" span>{ta("work_history", 3, "Executive Assistant | Acme Co | 2021 - Present | Managed CEO calendar")}</F>
          <F label="Education — one per line: Degree | School | Year" span>{ta("education", 2, "BA Business | University of Manila | 2019")}</F>
        </div>
        <DialogFooter><Button data-testid="cf-save-button" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save candidate"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
